"use client";

import React, { useEffect, useRef, useState } from "react";
import { BookOpen, Car, Flag, Snowflake, Target, Trophy } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const interestsData = [{
  id: "skiing",
  title: "Skiing",
  description: "Having skied most of my life I have found that the sport not only builds your endurance and overall athleticism but also allows for growth socially.",
  icon: Snowflake,
  image: "https://up-drum.10web.cloud/wp-content/uploads/2026/03/953A2A49-AA19-4D2D-8201-AD6223730FFD.jpg?v=s6ljoj4lzpl",
  tags: ["Winter", "Endurance", "Focus"]
}, {
  id: "golf",
  title: "Golf",
  description: "I have recently fallen in love with the game of Golf. I have found that the game allows you to constantly improve in both the game and how you utilize it. The sport teaches lessons in consistency and percision and can teach you about yourself well into retirement.\xA0",
  icon: Target,
  image: "https://up-drum.10web.cloud/wp-content/uploads/2026/03/6996A598-9F1F-4749-A119-0F24210052DA_1_102_o.jpeg?v=hgvhi4fvy7l",
  tags: ["Precision", "Strategy", "Patience"]
}, {
  id: "reading",
  title: "Reading",
  description: "I have recently utilized reading to avoid the constant pull that social media can have on us with an activity that is not only good for the brain but can teach you about yourself.",
  icon: BookOpen,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600191/img/reading_classic_literature.png",
  tags: ["Knowledge", "Curiosity", "Analysis"]
}, {
  id: "formula-one",
  title: "Formula One",
  description: "I began following Formula One not only for the excitement of motor sport but also the teamwork and incredible enginieering on display.",
  icon: Car,
  image: "https://up-drum.10web.cloud/wp-content/uploads/2026/03/A646154E-DD68-4673-A978-5B6813089087_1_105_c.jpeg?v=uyqpswgtvhi",
  tags: ["Data-Driven", "Innovation", "Global"]
}, {
  id: "football",
  title: "Football",
  description: "Watching football has become amoung my favorite pass times in recent years whether it is the University of Michigan or the Lions.",
  icon: Flag,
  image: "https://up-drum.10web.cloud/wp-content/uploads/2026/03/F3A2C1B4-DC54-4B14-8E3D-4BD9B9AFEBD2_1_105_c.jpeg?v=kyqy5rfrzba",
  tags: ["Teamwork", "Tactics", "Resilience"]
}, {
  id: "basketball",
  title: "Basketball",
  description: "I have also fallen in love with watching the Pistons or wolverines and have been attending games well into my childhood.",
  icon: Trophy,
  image: "https://up-drum.10web.cloud/wp-content/uploads/2026/03/0A7CCBA2-8ABE-4B46-B1E0-4000A28E85EC_1_105_c.jpeg?v=yvfrmkr3539",
  tags: ["Agility", "Communication", "Dynamic"]
}];
export default function Interests() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <section id="interests" className="bg-background py-24 sm:py-32 overflow-hidden">
      <div ref={ref} className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="mb-16 max-w-3xl">
          <h2 className="font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl mb-6">
            Personal Interests
          </h2>
          <p className="font-sans text-lg text-muted-foreground leading-relaxed">Beyond academic and professional pursuits, these activities have shaped who I am and has allowed me to grow and build relationships that I would have been unable to previously.</p>
        </div>

        <Tabs defaultValue="skiing" className="w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {}
            <div className="lg:col-span-4 xl:col-span-3">
              <TabsList className="flex flex-col h-auto w-full bg-transparent p-0 gap-3">
                {interestsData.map((interest, i) => {
                const Icon = interest.icon;
                return <TabsTrigger key={interest.id} value={interest.id} data-index={i} className="w-full justify-start gap-4 px-6 py-4 h-auto rounded-xl border border-border/60 bg-card text-card-foreground shadow-sm transition-all hover:border-primary/40 hover:shadow-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg font-sans text-base font-medium">
                      <Icon className="size-5 shrink-0 opacity-80" />
                      <span>{interest.title}</span>
                    </TabsTrigger>;
              })}
              </TabsList>
            </div>

            {}
            <div className="lg:col-span-8 xl:col-span-9">
              {interestsData.map((interest, i) => <TabsContent key={interest.id} value={interest.id} data-index={i} className="mt-0 outline-none focus-visible:ring-0">
                  <Card className="overflow-hidden border-border/50 shadow-xl rounded-2xl bg-card">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                      
                      {}
                      <div className="relative w-full h-64 md:h-auto bg-muted">
                        <AspectRatio ratio={4 / 3} className="h-full w-full">
                          <img src={interest.image} alt={interest.title} className="object-cover w-full h-full absolute inset-0" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                        </AspectRatio>
                      </div>

                      {}
                      <div className="flex flex-col justify-center p-8 lg:p-12">
                        <CardHeader className="p-0 mb-6">
                          <div className="flex items-center gap-3 mb-4 text-primary">
                            <interest.icon className="size-6" />
                            <Separator orientation="vertical" className="h-6 bg-primary/30" />
                            <span className="font-mono text-sm tracking-wider uppercase font-semibold">
                              Hobby & Passion
                            </span>
                          </div>
                          <CardTitle className="font-serif text-3xl md:text-4xl text-foreground leading-tight">
                            {interest.title}
                          </CardTitle>
                        </CardHeader>
                        
                        <CardContent className="p-0 flex flex-col gap-8">
                          <p className="font-sans text-muted-foreground text-base md:text-lg leading-relaxed">
                            {interest.description}
                          </p>
                          
                          <div className="flex flex-wrap gap-2 mt-auto">
                            {interest.tags.map((tag, tagIndex) => <Badge key={tagIndex} variant="secondary" data-index={tagIndex} className="font-mono text-xs px-3 py-1 bg-muted text-muted-foreground hover:bg-muted/80 border-border/50">
                                {tag}
                              </Badge>)}
                          </div>
                        </CardContent>
                      </div>

                    </div>
                  </Card>
                </TabsContent>)}
            </div>

          </div>
        </Tabs>
      </div>
    </section>;
}